import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  LuAlertTriangle,
  LuArrowRight,
  LuCheckCircle2,
  LuClipboardList,
  LuPackage,
  LuShoppingCart,
  LuTrendingDown,
} from 'react-icons/lu';
import { apiJson } from '../apiClient';
import { useProducts } from '../ProductContext';
import { useAuth } from '../AuthContext';
import { formatQtyWithUnit } from '../constants/productUnits';
import { isBrickFactoryUser } from '../utils/businessType';
import { buildLowStockInsights } from '../utils/lowStockInsights';
import { normalizeRole } from '../utils/userLocale';
import './LowStockAlertsPage.css';

const SEVERITY_LABEL = {
  critical: 'Imeisha',
  urgent: 'Chini sana',
  warning: 'Chini ya kiwango',
};

const LowStockAlertsPage = ({ onNavigate }) => {
  const { user } = useAuth();
  const factoryMode = isBrickFactoryUser(user);
  const isOwner = normalizeRole(user?.role) === 'owner';
  const { products, inventoryLoading } = useProducts();
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);

  const fetchOrders = useCallback(async () => {
    setOrdersLoading(true);
    try {
      const data = await apiJson(isOwner ? '/orders' : '/my-orders');
      setOrders(Array.isArray(data.orders) ? data.orders : []);
    } catch {
      setOrders([]);
    } finally {
      setOrdersLoading(false);
    }
  }, [isOwner]);

  useEffect(() => {
    if (!inventoryLoading) fetchOrders();
  }, [inventoryLoading, fetchOrders]);

  const insights = useMemo(
    () => buildLowStockInsights({ products, factoryMode, orders }),
    [products, factoryMode, orders]
  );

  const { alerts, stats, categoryBreakdown, recommendations, summary } = insights;
  const loading = inventoryLoading || ordersLoading;

  const go = (key) => {
    if (typeof onNavigate === 'function') onNavigate(key);
  };

  if (loading) {
    return (
      <section className="ls-alerts page-bleed">
        <p className="ls-alerts__muted">Loading insights…</p>
      </section>
    );
  }

  return (
    <section className="ls-alerts page-bleed">
      <header className="ls-alerts__hero">
        <div className="ls-alerts__hero-icon" aria-hidden>
          <LuTrendingDown size={22} />
        </div>
        <div>
          <h1 className="ls-alerts__title">
            {factoryMode ? 'Low stock alerts' : 'Low Stock Alerts'}
          </h1>
          <p className="ls-alerts__sub">
            {factoryMode
              ? 'Analytics na mapendekezo kwa malighafi chini ya kiwango cha chini. Angalia hali, kisha agiza au pokea delivery.'
              : 'Analytics and recommendations for items below their minimum stock level.'}
          </p>
        </div>
      </header>

      <div className="ls-alerts__top">
        <article className={`ls-alerts__health ls-alerts__health--${stats.band.tone}`}>
          <div className="ls-alerts__health-ring" style={{ '--score': stats.healthScore }}>
            <span className="ls-alerts__health-value">{stats.healthScore}%</span>
          </div>
          <div>
            <span className="ls-alerts__health-label">Stock health</span>
            <strong className="ls-alerts__health-band">{stats.band.label}</strong>
            <p className="ls-alerts__health-summary">{summary}</p>
          </div>
        </article>

        <div className="ls-alerts__stats">
          <article className="ls-alerts__stat ls-alerts__stat--warn">
            <LuAlertTriangle size={18} aria-hidden />
            <div>
              <span className="ls-alerts__stat-label">Alerts</span>
              <strong>{stats.alertCount}</strong>
            </div>
          </article>
          <article className="ls-alerts__stat ls-alerts__stat--danger">
            <LuPackage size={18} aria-hidden />
            <div>
              <span className="ls-alerts__stat-label">Out of stock</span>
              <strong>{stats.outOfStock}</strong>
            </div>
          </article>
          <article className="ls-alerts__stat ls-alerts__stat--info">
            <LuClipboardList size={18} aria-hidden />
            <div>
              <span className="ls-alerts__stat-label">On order</span>
              <strong>{stats.onOrderCount}</strong>
            </div>
          </article>
          <article className="ls-alerts__stat ls-alerts__stat--good">
            <LuCheckCircle2 size={18} aria-hidden />
            <div>
              <span className="ls-alerts__stat-label">Healthy</span>
              <strong>
                {stats.healthyCount}/{stats.totalItems}
              </strong>
            </div>
          </article>
        </div>
      </div>

      {recommendations.length > 0 ? (
        <section className="ls-alerts__advice" aria-labelledby="ls-advice-title">
          <h2 id="ls-advice-title" className="ls-alerts__section-title">
            What to do next
          </h2>
          <ul className="ls-alerts__advice-list">
            {recommendations.map((rec, idx) => (
              <li key={idx} className={`ls-alerts__advice-item ls-alerts__advice-item--${rec.tone}`}>
                {rec.text}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {categoryBreakdown.length > 0 ? (
        <section className="ls-alerts__categories" aria-label="Alerts by category">
          {categoryBreakdown.map((c) => (
            <span key={c.label} className="ls-alerts__cat-chip">
              {c.label} <strong>{c.count}</strong>
            </span>
          ))}
        </section>
      ) : null}

      {alerts.length === 0 ? (
        <div className="ls-alerts__empty">
          <LuCheckCircle2 size={40} aria-hidden />
          <p>Everything is above the low-stock threshold.</p>
          {factoryMode ? (
            <button type="button" className="ls-alerts__btn ls-alerts__btn--ghost" onClick={() => go('factory-stock-overview')}>
              View stock overview
            </button>
          ) : null}
        </div>
      ) : (
        <div className="ls-alerts__grid">
          {alerts.map((a) => (
            <article key={a.id} className={`ls-alerts__card ls-alerts__card--${a.severity}`}>
              <div className="ls-alerts__card-top">
                <span className={`ls-alerts__severity ls-alerts__severity--${a.severity}`}>
                  {SEVERITY_LABEL[a.severity]}
                </span>
                {a.categoryShort ? (
                  <span className="ls-alerts__card-cat">{a.categoryShort}</span>
                ) : null}
              </div>
              <h2 className="ls-alerts__card-name">{a.name}</h2>
              <div className="ls-alerts__meter" aria-hidden>
                <span className="ls-alerts__meter-fill" style={{ width: `${a.fillPct}%` }} />
              </div>
              <dl className="ls-alerts__card-meta">
                <div>
                  <dt>On hand</dt>
                  <dd>{formatQtyWithUnit(a.quantity, a.unit)}</dd>
                </div>
                <div>
                  <dt>Min level</dt>
                  <dd>{formatQtyWithUnit(a.limit, a.unit)}</dd>
                </div>
                <div>
                  <dt>Shortfall</dt>
                  <dd>{a.shortfall > 0 ? formatQtyWithUnit(a.shortfall, a.unit) : '—'}</dd>
                </div>
              </dl>
              {a.onOrder ? (
                <p className="ls-alerts__on-order">
                  On order: <strong>{a.onOrder.poNumber}</strong> ({a.onOrder.status.replace(/_/g, ' ')})
                </p>
              ) : (
                <p className="ls-alerts__on-order ls-alerts__on-order--none">Not on any open order</p>
              )}
            </article>
          ))}
        </div>
      )}

      {factoryMode ? (
        <div className="ls-alerts__actions">
          <button type="button" className="ls-alerts__btn ls-alerts__btn--primary" onClick={() => go('orders-make')}>
            <LuShoppingCart size={16} aria-hidden /> Order materials
          </button>
          <button type="button" className="ls-alerts__btn" onClick={() => go('orders-tracking')}>
            Tracking status <LuArrowRight size={14} aria-hidden />
          </button>
          <button type="button" className="ls-alerts__btn ls-alerts__btn--ghost" onClick={() => go('factory-stock-overview')}>
            Stock overview
          </button>
        </div>
      ) : stats.alertCount > 0 ? (
        <div className="ls-alerts__actions">
          <button type="button" className="ls-alerts__btn ls-alerts__btn--primary" onClick={() => go('orders-make')}>
            Create purchase order
          </button>
        </div>
      ) : null}
    </section>
  );
};

export default LowStockAlertsPage;
